import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThumbsUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { useGetSuggestions, useVoteSuggestion } from '@/application/services/suggestion.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { Button } from '@/ui/components/common/Button'
import { markdownToPlainExcerpt } from '@/ui/utils/markdown'

const SUGGESTION_PAGE_SIZE = 10

/**
 * Community suggestions block: sort tabs (votes/date), paginated suggestions table
 * with vote buttons. The "suggest" CTA and dialog are handled by the parent page.
 */
export function SuggestionsSection() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()

  const [suggestionSortBy, setSuggestionSortBy] = useState<'votes' | 'date'>('votes')
  const [suggestionPage, setSuggestionPage] = useState(1)

  const { data: suggestionsData } = useGetSuggestions(suggestionSortBy)
  const { mutate: vote } = useVoteSuggestion()

  const allSuggestions = suggestionsData?.suggestions ?? []
  const suggestionTotalPages = Math.max(1, Math.ceil(allSuggestions.length / SUGGESTION_PAGE_SIZE))
  const pagedSuggestions = allSuggestions.slice(
    (suggestionPage - 1) * SUGGESTION_PAGE_SIZE,
    suggestionPage * SUGGESTION_PAGE_SIZE,
  )

  return (
    <section>
      <div className="mb-6">
        <h2 className="text-2xl font-serif font-bold">{t('investigations.suggestions_title')}</h2>
        <p className="text-muted-foreground text-sm mt-1">
          {t('investigations.suggestions_subtitle')}
        </p>
      </div>

      {!isAuthenticated && (
        <div className="mb-6 text-center py-4 bg-background border border-border rounded-lg text-sm text-muted-foreground">
          {t('investigations.login_to_suggest')}{' '}
          <Link to="/login" className="text-primary hover:underline">
            {t('auth.login_link')}
          </Link>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <Button
          variant={suggestionSortBy === 'votes' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => {
            setSuggestionSortBy('votes')
            setSuggestionPage(1)
          }}
        >
          {t('investigations.sort_most_voted')}
        </Button>
        <Button
          variant={suggestionSortBy === 'date' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => {
            setSuggestionSortBy('date')
            setSuggestionPage(1)
          }}
        >
          {t('investigations.sort_newest')}
        </Button>
      </div>

      {suggestionsData && suggestionsData.suggestions.length > 0 ? (
        <>
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    {t('investigations.suggestion_col_title')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                    {t('investigations.suggestion_col_by')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                    {t('investigations.suggestion_col_date')}
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    {t('investigations.suggestion_col_votes')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedSuggestions.map((suggestion) => (
                  <tr
                    key={suggestion.suggestionId}
                    className="border-b border-border last:border-0 hover:bg-background/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{suggestion.title}</div>
                      {suggestion.description && (
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {markdownToPlainExcerpt(suggestion.description)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                      {suggestion.suggestedBy}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                      {new Date(suggestion.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-bold text-primary">{suggestion.voteCount}</span>
                        {isAuthenticated ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => vote(suggestion.suggestionId)}
                            className="h-7 px-2 text-xs"
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {t('investigations.login_to_vote')}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {suggestionTotalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSuggestionPage((page) => Math.max(1, page - 1))}
                disabled={suggestionPage === 1}
                leftIcon={<ChevronLeft className="h-4 w-4" />}
              >
                {t('home.carousel_prev')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('admin.pagination_info', {
                  page: suggestionPage,
                  total: suggestionTotalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setSuggestionPage((page) => Math.min(suggestionTotalPages, page + 1))
                }
                disabled={suggestionPage >= suggestionTotalPages}
                rightIcon={<ChevronRight className="h-4 w-4" />}
              >
                {t('home.carousel_next')}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-10 text-muted-foreground border border-border rounded-lg text-sm">
          {t('investigations.suggestion_empty')}
        </div>
      )}
    </section>
  )
}
